import {
  forwardRef,
  useEffect,
  useState
} from 'react';
import cn from 'classnames';
import PropTypes from 'prop-types';

import LdsValidationError from '../LdsValidationError/index.jsx';

const LdsTextarea = forwardRef(function LdsTextarea({
  id,
  name,
  label,
  ariaDescribedBy = '',
  counterHiddenLabel,
  counterText,
  disabled = false,
  error = false,
  errorMessage,
  maxLength,
  onChange,
  className,
  required,
  ...rest
}, ref) {
  const [charCount, setCharCount] = useState(0);
  const [describedBy, setDescribedBy] = useState(ariaDescribedBy);

  const errorId = `${id}-textarea-error`;

  useEffect(() => {
    let descStr = ariaDescribedBy;
    if (maxLength) {
      descStr = `${id}-char-count ${ariaDescribedBy}`;
    }
    if (error && errorMessage) descStr += ` ${errorId}`;
    setDescribedBy(descStr.trim());
  }, [ariaDescribedBy, maxLength, error, errorMessage, errorId, id]);

  const CharacterCount = () => {
    if (!maxLength) return '';

    return (
      <>
      <span
        id={`${id}-char-count`}
        className="sr-only"
      >
        {counterHiddenLabel || `${maxLength} character limit`}
      </span>
      <span
        role="status"
        aria-live="polite"
        className={cn(
          'lds-textarea-counter',
          {
            disabled: disabled,
            error: error,
          }
        )}
      >
        {charCount} {counterText || `of ${maxLength}`}
      </span>
      </>
    );
  };

  const handleChange = (e) => {
    setCharCount(e.target.value.length);
    if (onChange) onChange(e);
  };

  return (
    <div
      className={cn(
        'lds-textarea-wrapper',
        className
      )}
    >
      <label
        htmlFor={id}
        className={cn(
          'lds-form-label',
          'lds-textarea-label',
          {
            disabled: disabled,
          }
        )}
      >
        {label}
      </label>
      <CharacterCount />
      <textarea
        id={id}
        name={name}
        disabled={disabled}
        onChange={handleChange}
        maxLength={maxLength}
        aria-invalid={error}
        aria-describedby={describedBy}
        className={cn(
          'lds-textarea',
          {
            disabled: disabled,
            error: error,
          }
        )}
        required={required}
        aria-required={required ? true : null}
        {...rest}
        ref={ref}
      />
      {
        error && errorMessage
        && <LdsValidationError id={errorId}>{errorMessage}</LdsValidationError>
      }
    </div>
  );
});

LdsTextarea.prototype = {
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  error: PropTypes.bool,
  maxLength: PropTypes.any,
  required: PropTypes.bool,
};

export default LdsTextarea;
